/* lib/adapters/notifications.js — адаптеры уведомлений к боевому бекенду.
 *  • adaptNotification — добавляет workspace_name (join по workspace_id из AuthProvider.workspaces)
 *    и data.destination (бэк отдаёт сырые id — destination выводим по type+data).
 *  • notificationsReal — getPreferences/updatePreference для NotificationsTab (через unwrap→throw,
 *    т.к. таб использует useFakeQuery). Бэк отдаёт label, но НЕ description → добавляем client-side. */

import { unwrap } from '../api/unwrap.js';
import * as api from '../api/notifications.js';
import { isPathAllowed } from '../planSections.js';
import { REPORTS_SECTION_ENABLED, reportHref } from '../reportsSection.js';

// id пространства из data: целое > 0 (число или строка цифр), иначе null.
function positiveId(v) {
  if (typeof v === 'number') return Number.isSafeInteger(v) && v > 0 ? v : null;
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) {
    const n = Number(v.trim());
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  }
  return null;
}

// Тариф ТОГО пространства, о котором уведомление. `?tab=subscription` ВСЕГДА открывает
// личный тариф, а тариф заведения живёт в «Заведения → Тариф» (BusinessSection выбирает
// заведение по ?ws=). Адрес строим сами из вида и id пространства — готовый путь из data
// не берём. Нет вида (история, прочие типы) — как раньше, личный тариф.
// Зеркало бэкенда — notifications/services.py:tariff_path.
export function tariffDestination(d) {
  const data = d || {};
  if (data.workspace_kind === 'business') {
    const id = positiveId(data.workspace_id);
    return id ? `/settings?tab=business&biz=tariff&ws=${id}` : '/settings?tab=business&biz=tariff';
  }
  return '/settings?tab=subscription';
}

// Маршрут перехода по уведомлению (бэк хранит сырые id в data — см. notifications/services.py).
export function deriveDestination(n) {
  const d = (n && n.data) || {};
  switch (n && n.type) {
    case 'report_ready': return reportHref(d.report_id);   // №524: раздел выключен — без ссылки
    case 'invitation_received': return d.invitation_token ? `/invite/accept/${d.invitation_token}` : null;
    // Одно меню — в его редактор. Пачка (массовая смена статуса) приходит одним сводным
    // уведомлением с data.menu_ids — у него нет одного меню: в реестр «Проекты меню».
    // Ключи кладёт ttk/menu_services.py (notify_menu_published / notify_menus_published).
    case 'menu_published':
      if (d.menu_id != null) return `/kitchen?tab=menus&edit=menu-${d.menu_id}`;
      return Array.isArray(d.menu_ids) && d.menu_ids.length ? '/kitchen?tab=menus' : null;
    case 'ttk_cost_stale': return d.ttk_id != null ? `/kitchen?tab=recipes&edit=ttk-${d.ttk_id}` : null;
    // Окончание подписки: «заканчивается» (за 3 дня и в день окончания), «закончилась»
    // (после окончания), подарочный тариф до окончания, раннее напоминание.
    case 'subscription_expiring':
    case 'subscription_expired':
    case 'gift_expiring':
    case 'dealer_plan_granted':
    case 'subscription_expiring_early': return tariffDestination(d);
    case 'payment_succeeded':
    case 'payment_failed':
    // История: прежние окна «за 7 дней» / «за 1 день» больше не приходят, но их строки
    // остались в инбоксе — переход у них прежний.
    case 'subscription_expiring_7d':
    case 'subscription_expiring_1d': return '/settings?tab=subscription';
    case 'member_joined':
    case 'member_left': return '/settings?tab=team';
    case 'price_spike_alert': return '/analytics';
    // Академия. Ключи data кладёт academy/services.py — маршруты обязаны зеркалить
    // их байт-в-байт: разъехавшийся ключ даёт не ошибку, а «клик в никуда».
    // Дрип-уведомление об открытии модуля по расписанию (#12) → в плеер курса.
    case 'academy_lesson_opened':
    case 'academy_course_assigned':
    case 'academy_course_purchased':
      return d.course_id != null ? `/learning/c/${d.course_id}` : null;
    // Вердикт по ДЗ → сразу в нужный урок, а не в оглавление курса.
    case 'academy_homework_reviewed':
      if (d.course_id == null) return null;
      return d.lesson_id != null
        ? `/learning/c/${d.course_id}?lesson=${d.lesson_id}`
        : `/learning/c/${d.course_id}`;
    // Куратору — в очередь проверки, с раскрытой сдачей.
    case 'academy_homework_submitted':
      return d.submission_id != null
        ? `/console/academy?tab=review&review=${d.submission_id}`
        : '/console/academy?tab=review';
    case 'academy_certificate_issued':
      return d.certificate_public_id ? `/certificate/${d.certificate_public_id}` : null;
    default: return null;
  }
}

export function adaptNotification(n, wsNameMap) {
  const wsName = (n.workspace_id != null && wsNameMap && wsNameMap[n.workspace_id]) || '';
  return {
    id: n.id, type: n.type, title: n.title, body: n.body || '',
    created_at: n.created_at, is_read: !!n.is_read,
    workspace_id: n.workspace_id, workspace_name: wsName,
    data: { ...(n.data || {}), destination: deriveDestination(n) },
  };
}

// Адрес «Перейти», не ведущий в раздел, который закрыл тариф АКТИВНОГО пространства
// (price_spike_alert → /analytics при workspace.sections.analytics === false), убираем:
// клик по нему упёрся бы в редирект гарда. Уведомление остаётся, но без действия.
// Переход выполняется в активном пространстве (клик его не переключает) — по нему и гейтим.
// Нечего менять — возвращаем ТОТ ЖЕ объект (стабильные ссылки для useMemo потребителей).
export function gateNotificationDestination(n, workspace) {
  const dest = n && n.data && n.data.destination;
  if (!dest || isPathAllowed(dest, workspace)) return n;
  return { ...n, data: { ...n.data, destination: null } };
}

/* Описания типов для NotificationsTab (бэк list_preferences отдаёт label, но не
   description). Каталог зеркалит notifications/models.py:NOTIFICATION_TYPES —
   ЦЕЛИКОМ: строка без описания в списке настроек выглядит недоделкой, и шесть
   таких (сборка меню, обе поддержки, оба онбординга, счёт на оплату) висели
   пустыми. Экспортируем, чтобы тест сверял каталог с бэкендом. */
export const PREF_DESCRIPTIONS = {
  invitation_received: 'Когда вас приглашают в команду пространства.',
  payment_succeeded: 'Успешные платежи по подписке.',
  payment_failed: 'Неуспешные попытки оплаты.',
  subscription_expiring: 'За 3 дня до окончания подписки и в день окончания.',
  subscription_expired: 'После окончания подписки: на следующий день и в последний день доступа к платным разделам.',
  // Приходит только владельцу тарифа с ранним напоминанием (выдан без оплаты), но
  // строка видна в списке настроек всем, поэтому адресат назван прямо в тексте.
  subscription_expiring_early: 'За несколько недель до окончания доступа — только на тарифах с ранним напоминанием.',
  report_ready: 'Когда готов AI-отчёт (food cost, ABC, инвентаризация).',
  price_spike_alert: 'Резкий рост закупочных цен на ингредиенты.',
  menu_published: 'Когда меню сделано действующим.',
  menu_build_done: 'Когда AI-сборка меню закончилась и блюда готовы.',
  ttk_cost_stale: 'Когда себестоимость ТТК могла измениться.',
  member_joined: 'Новый участник присоединился к команде.',
  member_left: 'Участник покинул команду.',
  // Поддержка. Первая строка приходит только операторам — в списке она видна и
  // обычному пользователю, поэтому адресат назван прямо в тексте.
  support_user_message: 'Пользователь написал в поддержку (для операторов).',
  support_operator_reply: 'Когда оператор поддержки ответил вам.',
  // Онбординг: события первого запуска — их ждут ровно один раз.
  onboarding_assistant_available: 'Когда данные загружены и Аргус готов к работе.',
  onboarding_analytics_ready: 'Когда данные из кассы разобраны и аналитика собрана.',
  payment_link_issued: 'Когда менеджер выставил вам счёт или ссылку на оплату.',
  // Подарочные сертификаты: первая строка — дарителю, вторая — тому, кому подарили.
  gift_redeemed: 'Когда подаренный вами сертификат активировали.',
  gift_expiring: 'За 3 дня до окончания подаренного вам тарифа и в день окончания.',
  // Дилеры: клиент платит партнёру, и это его подтверждение, что тариф включён и до какой даты.
  dealer_plan_granted: 'Когда ваш партнёр подключил тариф заведению.',
  // Академия. Строка без описания выглядит как недоделка, поэтому описан КАЖДЫЙ
  // тип из каталога бэкенда (notifications/models.py) — включая кураторские.
  academy_lesson_opened: 'Когда по расписанию курса открывается новый модуль.',
  academy_course_assigned: 'Когда методист записал вас на курс.',
  academy_course_purchased: 'Подтверждение оплаты курса и открытия доступа.',
  academy_homework_reviewed: 'Когда куратор проверил ваше домашнее задание.',
  academy_homework_submitted: 'Ученик прислал домашнее задание на проверку (для кураторов).',
  academy_certificate_issued: 'Когда курс пройден и готов сертификат.',
};
// №524: пока раздел «Отчёты» выключен, строки «Отчёт готов» в настройках нет — отчётов не бывает.
const withDesc = (rows) => (rows || [])
  .filter((p) => REPORTS_SECTION_ENABLED || p.type !== 'report_ready')
  .map((p) => ({ ...p, description: PREF_DESCRIPTIONS[p.type] || '' }));

async function getPreferences() {
  const d = await unwrap(api.getNotificationPreferences());
  return withDesc(d.preferences || []);
}

async function updatePreference(type, patch) {
  const d = await unwrap(api.updateNotificationPreferences([{ type, ...patch }]));
  return withDesc(d.preferences || []);
}

export const notificationsReal = { getPreferences, updatePreference };
